import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
} from '@mui/material';

import { EligibilityData } from '../types';
import { getEligibilityDataById, updateEligibilityData, deleteEligibilityData } from '../services/eligibilityService';
import EligibilityForm from './components/EligibilityForm';
import dayjs, { Dayjs } from 'dayjs';

const EligibilityDetail = () => {
    const { eligibilityId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [eligibilityData, setEligibilityData] = useState<EligibilityData>({});
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!eligibilityId) return;

        const fetchEligibilityData = async () => {
            try {
                const data = await getEligibilityDataById(parseInt(eligibilityId));
                
                // Convert string dates to Dayjs objects
                const processedData: EligibilityData = {
                    ...data,
                    hireDate: data.hireDate ? dayjs(data.hireDate) : null,
                    dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth) : null,
                    effectiveDate: data.effectiveDate ? dayjs(data.effectiveDate) : null,
                    endDate: data.endDate ? dayjs(data.endDate) : null,
                    createdAt: data.createdAt ? dayjs(data.createdAt) : null,
                    updatedAt: data.updatedAt ? dayjs(data.updatedAt) : null,
                };
                setEligibilityData(processedData);
            } catch (error) {
                console.error('[EligibilityDetail] Error fetching eligibility data', error);
            } finally {
                setLoading(false);
            }
        };

        fetchEligibilityData();
    }, [eligibilityId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});
        
        if (!eligibilityId) return;

        try {
            await updateEligibilityData(parseInt(eligibilityId), eligibilityData);
            // Preserve search parameters when returning to eligibility list
            const currentParams = new URLSearchParams(searchParams);
            navigate(`/eligibility?${currentParams.toString()}`);
        } catch (error) {
            if (error instanceof Error && error.message) {
                try {
                    const errorData = JSON.parse(error.message);
                    if (errorData.Field && errorData.Message) {
                        setFieldErrors({ [errorData.Field]: errorData.Message });
                    }
                } catch {
                    console.error('[Error Updating Eligibility Data]', error);
                }
            }
        }
    };

    const handleDelete = async () => {
        if (!eligibilityId) return;
        
        if (window.confirm("Are you sure you want to delete this eligibility record?")) {
            try {
                await deleteEligibilityData(parseInt(eligibilityId));
                // Preserve search parameters when returning after delete
                const currentParams = new URLSearchParams(searchParams);
                navigate(`/eligibility?${currentParams.toString()}`);
            } catch (error) {
                console.error('[Error Deleting Eligibility Data]', error);
            }
        }
    };

    const handleDateChange = (field: keyof EligibilityData, value: Dayjs | null) => {
        setEligibilityData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleInputChange = (field: keyof EligibilityData, value: any) => {
        setEligibilityData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear field error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                Loading...
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
            <Card>
                <CardHeader title="Eligibility Data Details" />
                <CardContent>
                    <EligibilityForm
                        eligibilityData={eligibilityData}
                        fieldErrors={fieldErrors}
                        onSubmit={handleSubmit}
                        onInputChange={handleInputChange}
                        onDateChange={handleDateChange}
                        submitButtonText="Save Changes"
                        showDeleteButton={true}
                        onDelete={handleDelete}
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default EligibilityDetail;
